import { AppThunk } from '../../store';
import { batch } from 'react-redux';
import { isNil } from 'lodash';
import {
  LlmSubjectKind,
  PathModeKey,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil, isNotNil } from '../../../utils/types';
import {
  addNewAnalysisIncidentAction,
  clearAnalysisAction,
  editAnalysisIncidentAction,
  populateAnalysisAction,
  setFunnelShowInReportStateAction,
} from './index';
import { analysisSelector } from '../../selectors/analyses';
import { IIncident } from '../../../pages/Incidents/interfaces';
import { updateIncidentModalData } from '../modal';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';
import { llmPopulateSubjectSummary } from '../llm';
import {
  clearAllLoadingAnalysesAction,
  setLoadingAnalysesInStoreAction,
} from '../loadingAnalyses';
import {
  isScratchpadAnalysisId,
  isTransientAnalysisId,
} from '../../utils/transientAnalysis';

const currentAnalysisSelectorInstance = analysisSelector();

export const populateAnalysisThunk =
  ({ analysis }: { analysis: IAnalysisResponse }): AppThunk =>
  (dispatch, getState) => {
    batch(() => {
      const incidentTypes = getState().incidents?.incidentTypes;
      const reportSetting = getState().bootstrap?.reportSetting;
      dispatch(
        populateAnalysisAction({
          analysis,
          incidentTypes,
          reportSetting,
        }),
      );
      if (!!analysis.llmSummary) {
        dispatch(
          llmPopulateSubjectSummary({
            subjectKind: LlmSubjectKind.Analysis,
            subjectId: analysis.id,
            summary: analysis.llmSummary,
          }),
        );
      }
      const populatedAnalysis = currentAnalysisSelectorInstance(
        getState(),
        analysis.id,
      );
      const chartData = populatedAnalysis?.chartData;
      if (!isNil(chartData)) {
        // Special case for Path, mode=Top Paths: each top path funnel becomes
        // a transient analysis in the main analysis store in order to reuse
        // Funnel display logic
        if (
          chartData.queryType === QueryType.Path &&
          chartData.mode === PathModeKey.TopPaths
        ) {
          [chartData.base, ...chartData.slices]
            .filter(isNotNil)
            .flatMap((s) => s.topPaths)
            .forEach((tp) => {
              assertNotNil(tp.funnel);
              dispatch(
                populateAnalysisThunk({
                  analysis: {
                    ...tp.funnel,
                    incidents: [],
                    creator: chartData.creator,
                  },
                }),
              );
              dispatch(
                setFunnelShowInReportStateAction({
                  analysisId: tp.funnel.id,
                  showInReportState: chartData.canCreateFunnel,
                }),
              );
            });
        }
      }
    });
  };

export const addNewAnalysisIncidentThunk =
  (payload: { incident: IIncident | null; analysisId: string }): AppThunk =>
  (dispatch, getState) => {
    const incidentTypes = getState().incidents?.incidentTypes;
    dispatch(
      addNewAnalysisIncidentAction({
        ...payload,
        incidentTypes,
      }),
    );
  };

export const editAnalysisIncidentThunk =
  (payload: { incident: IIncident; analysisId: string }): AppThunk =>
  (dispatch, getState) => {
    const incidentTypes = getState().incidents?.incidentTypes;
    dispatch(
      editAnalysisIncidentAction({
        ...payload,
        incidentTypes,
      }),
    );
  };

export const updateIncidentModalDataThunk =
  (payload: { incident: IIncident }): AppThunk =>
  (dispatch, getState) => {
    const incidentTypes = getState().incidents?.incidentTypes;
    dispatch(
      updateIncidentModalData({
        ...payload,
        incidentTypes,
      }),
    );
  };

/**
 * A special thunk that retains only the analyses from payload.analysisIds in the store.
 * Any analysis which are not in payload.analysisIds will be removed.
 * Analyses which aren't yet in the store will be loaded.
 */
export const retainAnalysesThunk =
  (payload: { analysisIds: string[] }): AppThunk =>
  (dispatch, getState) => {
    const { analysisIds } = payload;
    const { analyses } = getState();

    dispatch(clearAllLoadingAnalysesAction());
    const alreadyLoadedAnalyses = new Set<string>();
    const analysesToRetain = new Set(analysisIds);

    for (const existingAnalysisId of Object.keys(analyses)) {
      if (
        !analysesToRetain.has(existingAnalysisId) &&
        !isScratchpadAnalysisId(existingAnalysisId) &&
        /**
         * This change is done to justify the case when the top paths are part of dashboard
         *
         * There is a potential memory leak here because the transient analyses from the top path
         * are not removed from the redux store when the parent analyse is deleted.
         * The headless transient analyses will be removed when the user switches to another dashboard.
         */
        !isTransientAnalysisId(existingAnalysisId)
      ) {
        dispatch(clearAnalysisAction(existingAnalysisId));
      } else {
        alreadyLoadedAnalyses.add(existingAnalysisId);
      }
    }

    dispatch(
      setLoadingAnalysesInStoreAction({
        analysisIds: analysisIds.filter((id) => !alreadyLoadedAnalyses.has(id)),
        refresh: false,
      }),
    );
  };
